import React, { useCallback, useState } from "react";

import Alert from "@busbud/design-system-components/dist/Alert/Alert";
import Button from "@busbud/design-system-components/dist/Button/Button";
import Heading from "@busbud/design-system-components/dist/Heading/Heading";
import Link from "@busbud/design-system-components/dist/Link/Link";
import Stack from "@busbud/design-system-components/dist/Stack/Stack";
import Text from "@busbud/design-system-components/dist/Text/Text";

import { NoDiacriticsInput } from "@app/components/diacritics";
import { useAppContext } from "@app/helpers/hooks";

import { toUserVisibleError } from "../../../../helpers/api-error";
import * as Validator from "../../../../helpers/input-validator";
import { getErrorText } from "../../../../modules/account/utils";
import { clickedForgotPassword } from "../../../../tracking/account-tracking";
import { useAnimation } from "../authentication.styles";

interface Props {
  from_expired_link?: boolean;
  goToSignIn: () => void;
}

export const ForgotPassword: React.FC<Props> = ({
  goToSignIn,
  from_expired_link = false
}) => {
  const { classes } = useAnimation();
  const { tracker, translator, api, locale } = useAppContext();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [has_request_succedeed, setSuccessfullyRequested] = useState(false);
  const [error, setError] = useState<string>();

  function handleForgotPassword(event: React.MouseEvent) {
    tracker.track(clickedForgotPassword());
    event.preventDefault();
    const email_error = Validator.email(email);
    if (email_error) {
      setError(getErrorText(translator, "email.invalid_email"));
      return;
    }
    setError(undefined);
    setSubmitted(true);

    api
      .requestPasswordReset({ locale, email })
      .then(() => {
        setError(undefined);
        setSuccessfullyRequested(true);
      })
      .catch(err => {
        if (err.response.status === 429) {
          const error = getErrorText(translator, "TooManyRequests");
          setError(error);
          return;
        }

        const bb_err = toUserVisibleError(err);
        if (bb_err) {
          const error = getErrorText(translator, bb_err.name);
          setError(error);
          setSubmitted(false);
        }
      });
  }

  const getErrorMessage = useCallback(
    (error: string | undefined, from_expired_link: boolean) => {
      if (!!from_expired_link) {
        return getErrorText(translator, "PasswordResetTokenInvalid");
      }
      if (!!error) {
        return error;
      }
    },
    [error, from_expired_link]
  );

  return (
    <Stack spacing={2} direction="column" classes={{ root: classes.container }}>
      <Heading size="sm" component="h2" align="center" fontWeight="heavy">
        {translator.t("!account.forgot-password.title")}
      </Heading>

      <Text size="sm" lead align="center">
        {translator.t("!account.forgot-password.text")}
      </Text>

      {(!!error || !!from_expired_link) && (
        <Alert severity="negative">
          {getErrorMessage(error, from_expired_link)}
        </Alert>
      )}

      {!!has_request_succedeed && (
        <Alert severity="positive">
          {translator.t(
            "!account.forgot-password.password-reset-requested.success-message"
          )}
        </Alert>
      )}

      <form>
        <Stack spacing={1} direction="column">
          <NoDiacriticsInput
            type="email"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- TODO: [fix-jsx-a11y]
            autoFocus
            className="w-full"
            labelText={translator.t("!account.signin.email")}
            value={email}
            isDisabled={submitted}
            isInvalid={!!error}
            onChange={e => setEmail(e.target.value)}
          />
          <Button
            id="submit"
            type="submit"
            variant="contained"
            size="lg"
            fullWidth
            disabled={submitted}
            onClick={handleForgotPassword}
          >
            {translator.t("!account.forgot-password.submit")}
          </Button>
        </Stack>
      </form>

      <Link
        size="lg"
        color="palette.blue.regular.500"
        align="center"
        fontWeight="bold"
        onClick={goToSignIn}
      >
        {translator.t("!account.forgot-password.back-to-sign-in")}
      </Link>
    </Stack>
  );
};
